/**
 * Invites State
 *
 * NGXS state for managing user invites data.
 * Subscribes to Firestore invites document for the logged-in user
 * and provides selectors for accessing invites throughout the application.
 */

import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import {
  InitializeInvites,
  InvitesLoaded,
  InvitesLoadFailure,
  ClearInvites,
  SetInvitesLoading,
} from './invites.actions';
import { Invite } from 'src/app/models/schema';

/**
 * Invites State Model
 *
 * State shape for the invites store
 */
export interface InvitesStateModel {
  /** Array of invites for the current user */
  invites: Invite[];
  /** Whether invites data is currently loading */
  loading: boolean;
  /** Error message if invites operation failed */
  error: string | null;
  /** Whether invites data has been loaded */
  loaded: boolean;
  /** Email address of the user whose invites are loaded */
  email: string | null;
}

/**
 * Invites State
 *
 * Manages user invites data in the NGXS store
 */
@State<InvitesStateModel>({
  name: 'invites',
  defaults: {
    invites: [],
    loading: false,
    error: null,
    loaded: false,
    email: null,
  },
})
@Injectable()
export class InvitesState {
  /**
   * Select the entire invites state
   */
  @Selector()
  static getState(state: InvitesStateModel): InvitesStateModel {
    return state;
  }

  /**
   * Select the invites array
   */
  @Selector()
  static invites(state: InvitesStateModel): Invite[] {
    return state.invites;
  }

  /**
   * Select the loading state
   */
  @Selector()
  static loading(state: InvitesStateModel): boolean {
    return state.loading;
  }

  /**
   * Select the loaded state
   */
  @Selector()
  static loaded(state: InvitesStateModel): boolean {
    return state.loaded;
  }

  /**
   * Select the error state
   */
  @Selector()
  static error(state: InvitesStateModel): string | null {
    return state.error;
  }

  /**
   * Select the email for which invites are loaded
   */
  @Selector()
  static email(state: InvitesStateModel): string | null {
    return state.email;
  }

  /**
   * Select the count of invites
   */
  @Selector()
  static count(state: InvitesStateModel): number {
    return state.invites.length;
  }

  /**
   * Select pending invites only
   */
  @Selector()
  static pendingInvites(state: InvitesStateModel): Invite[] {
    return state.invites.filter(invite => invite.status === 'pending');
  }

  /**
   * Select count of pending invites
   */
  @Selector()
  static pendingCount(state: InvitesStateModel): number {
    return state.invites.filter(invite => invite.status === 'pending').length;
  }

  /**
   * Initialize invites listener for a user
   */
  @Action(InitializeInvites)
  initializeInvites(ctx: StateContext<InvitesStateModel>, action: InitializeInvites) {
    ctx.patchState({
      loading: true,
      error: null,
      email: action.email.toLowerCase(),
    });
  }

  /**
   * Handle successful invites load
   */
  @Action(InvitesLoaded)
  invitesLoaded(ctx: StateContext<InvitesStateModel>, action: InvitesLoaded) {
    ctx.patchState({
      invites: action.invites,
      loading: false,
      loaded: true,
      error: null,
    });
  }

  /**
   * Handle invites load failure
   */
  @Action(InvitesLoadFailure)
  invitesLoadFailure(ctx: StateContext<InvitesStateModel>, action: InvitesLoadFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Clear invites state
   */
  @Action(ClearInvites)
  clearInvites(ctx: StateContext<InvitesStateModel>) {
    ctx.setState({
      invites: [],
      loading: false,
      error: null,
      loaded: false,
      email: null,
    });
  }

  /**
   * Set loading state
   */
  @Action(SetInvitesLoading)
  setLoading(ctx: StateContext<InvitesStateModel>, action: SetInvitesLoading) {
    ctx.patchState({
      loading: action.loading,
    });
  }
}

